import { useCallback, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { authSelector, setAuth } from "@/pages/Public/redux/auth.slice";
import { useLoginAsMutation } from "@/pages/Private/redux/admin/admin.api";

/** The localStorage key the sidebar footer reads for "Back" (its `initialUser` reset). */
export const INITIAL_USER_STORAGE_KEY = "initialUser";

/**
 * Log in as another user and hard-navigate to `target`: stash the staff session under
 * `initialUser` so the sidebar footer's "Back" restores it, swap the auth state, reload the app at
 * the target. These are the mechanics `useLoginAsAndNavigate` always had; which user and which
 * landing page are the caller's call (PREC-3940 decision 3). A failed request undoes the stash, so
 * the footer does not read "Back" while you are still yourself.
 */
export const useLoginAs = () => {
	const dispatch = useAppDispatch();
	const userTokenObj = useAppSelector(authSelector);
	const [loginAsUser] = useLoginAsMutation();
	const [isLoading, setIsLoading] = useState(false);

	const loginAs = useCallback(
		async (userId: number, target: string): Promise<boolean> => {
			setIsLoading(true);
			localStorage.setItem(INITIAL_USER_STORAGE_KEY, JSON.stringify(userTokenObj));

			try {
				const newUser = await loginAsUser(userId).unwrap();

				dispatch(setAuth(newUser));
				window.location.href = target;

				return true;
			} catch {
				localStorage.removeItem(INITIAL_USER_STORAGE_KEY);

				return false;
			} finally {
				setIsLoading(false);
			}
		},
		[dispatch, userTokenObj, loginAsUser]
	);

	return { loginAs, isLoading };
};
